import React, { ReactNode, useRef } from 'react';
import { FieldNotify, SelectOption } from 'global';
import makeStyles from '@mui/styles/makeStyles';
import { FormNotificationBlock } from './FormNotificationBlock';
import { Box, CircularProgress, InputAdornment, MenuItem, TextField, TextFieldProps } from '@mui/material';

interface FormInputSelectProps<
  ValueType extends string | number | boolean = string,
  Options extends SelectOption<ValueType> = SelectOption<ValueType>,
> extends Omit<TextFieldProps, 'select' | 'onChange'> {
  label?: ReactNode;
  options?: Options[];
  isLoading?: boolean;
  notify?: FieldNotify;
  dataTestId?: string;
  customOptions?: ReactNode;
  onChange: (value: ValueType) => void;
}

export const FormInputSelect = <
  ValueType extends string | number | boolean = string,
  Options extends SelectOption<ValueType> = SelectOption<ValueType>,
>({
  label,
  value,
  error,
  options,
  onChange,
  isLoading,
  helperText,
  notify,
  dataTestId,
  customOptions,
  ...rest
}: FormInputSelectProps<ValueType, Options>) => {
  const inputRef = useRef<HTMLElement>();

  if (value && typeof value !== 'string' && options && options.length > 0 && typeof options[0].value === 'string') {
    value = String(value) as ValueType;
  }

  const classes = useStyles();

  const handleChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    event.preventDefault();
    event.stopPropagation();

    onChange(event.target.value as ValueType);
  };

  return (
    <Box style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', width: '100%' }}>
      <TextField
        select
        fullWidth
        size="small"
        label={label}
        value={value}
        error={error}
        variant="outlined"
        onChange={handleChange}
        className={classes.requiredInput}
        helperText={error ? helperText : ''}
        disabled={isLoading || !options?.length || rest?.inputProps?.disabled}
        {...rest}
        inputProps={{
          ref: (ref: any) => (inputRef.current = ref?.node),
          ...rest.inputProps,
        }}
        slotProps={{
          input: {
            endAdornment: isLoading ? (
              <InputAdornment position="end">
                <CircularProgress size={20} />
              </InputAdornment>
            ) : null,
          },
          select: {
            ref: rest.inputRef,
            MenuProps: {
              style: { maxHeight: '500px', minWidth: inputRef.current?.offsetWidth },
            },
            renderValue: selected => {
              const selectedOption = options?.find(option => option.value === selected);
              return selectedOption?.label ?? '';
            },
            IconComponent: isLoading ? () => null : undefined,
            ...(dataTestId ? { SelectDisplayProps: { 'data-testid': dataTestId } as any } : {}),
            ...rest.slotProps?.select,
          },
          ...rest.slotProps,
        }}
      >
        {customOptions
          ? customOptions
          : options?.map(option => (
              <MenuItem key={String(option.value)} value={option.value as any}>
                {option.label}
              </MenuItem>
            ))}
      </TextField>
      {notify && <FormNotificationBlock {...notify} />}
    </Box>
  );
};

const useStyles = makeStyles({
  requiredInput: {
    '& .MuiInputLabel-asterisk': {
      color: 'red',
    },
  },
});
